import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, API, fileUrl } from "@/lib/api";
import { UploadCloud, Loader2, Sparkles, Save, Download, Image as ImageIcon, FileText } from "lucide-react";
import { errorDetail } from "@/lib/utils";
import { TOKEN_KEY } from "@/lib/platform";

export default function AdminRebrand() {
  const [settings, setSettings] = useState({
    brand_name: "Heda Plywoods",
    logo_horizontal_color_file_id: null,
    logo_horizontal_white_file_id: null,
    logo_vertical_color_file_id: null,
    logo_vertical_white_file_id: null,
    contact_lines: [],
  });
  const [logoFiles, setLogoFiles] = useState({
    horizontal_color: null,
    horizontal_white: null,
    vertical_color: null,
    vertical_white: null,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [rebranding, setRebranding] = useState(false);
  const [progress, setProgress] = useState({ pages_done: 0, total_pages: 0, status: "", uploaded_pct: 0 });
  const [msg, setMsg] = useState("");
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    api.get("/brand-settings").then((r) => {
      setSettings({
        brand_name: r.data.brand_name || "Heda Plywoods",
        logo_horizontal_color_file_id: r.data.logo_horizontal_color_file_id || null,
        logo_horizontal_white_file_id: r.data.logo_horizontal_white_file_id || null,
        logo_vertical_color_file_id: r.data.logo_vertical_color_file_id || null,
        logo_vertical_white_file_id: r.data.logo_vertical_white_file_id || null,
        contact_lines: r.data.contact_lines || [],
      });
      setLogoFiles({
        horizontal_color: r.data.logo_horizontal_color_file || null,
        horizontal_white: r.data.logo_horizontal_white_file || null,
        vertical_color: r.data.logo_vertical_color_file || null,
        vertical_white: r.data.logo_vertical_white_file || null,
      });
    });
  }, []);

  const uploadLogo = async (file, slot) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "image");
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLogoFiles((x) => ({ ...x, [slot]: data }));
      setSettings((s) => ({ ...s, [`logo_${slot}_file_id`]: data.id }));
      setPreviewKey((k) => k + 1); // logo changed → bust the preview cache
    } catch (e) {
      alert(`Upload failed: ${e.message}`);
    }
  };

  const save = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/brand-settings", settings);
      setSettings(data);
      setMsg("Saved.");
      setPreviewKey((k) => k + 1); // contact/name changes → refresh preview
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      alert(errorDetail(e) || "Could not save");
    } finally {
      setSavingSettings(false);
    }
  };

  const rebrand = async (file) => {
    if (!file) return;
    setRebranding(true);
    setProgress({ pages_done: 0, total_pages: 0, status: "starting", uploaded_pct: 0 });
    try {
      const token = localStorage.getItem(TOKEN_KEY);

      // ---------- 1. Init a chunked upload session ----------
      // We upload the PDF in ~4 MB chunks so Cloudflare / nginx body-size
      // limits never reject a big brochure. Each chunk is a small POST.
      const initFd = new FormData();
      initFd.append("filename", file.name);
      const initRes = await fetch(`${API}/rebrand-jobs/upload/init`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: initFd,
      });
      if (!initRes.ok) {
        const raw = await initRes.text();
        let detail = null;
        try { detail = JSON.parse(raw)?.detail; } catch { detail = raw.replace(/<[^>]+>/g, " ").trim().slice(0, 160); }
        throw new Error(detail || `Server returned ${initRes.status} on upload init`);
      }
      const { upload_id, chunk_size } = await initRes.json();

      // ---------- 2. Slice + upload each chunk ----------
      const totalChunks = Math.ceil(file.size / chunk_size);
      for (let i = 0; i < totalChunks; i++) {
        const slice = file.slice(i * chunk_size, (i + 1) * chunk_size);
        const fd = new FormData();
        fd.append("index", String(i));
        fd.append("chunk", slice, `chunk-${i}`);
        // Retry each chunk up to 3 times on transient network failures.
        let ok = false;
        let lastErr = null;
        for (let attempt = 0; attempt < 3 && !ok; attempt++) {
          try {
            const r = await fetch(`${API}/rebrand-jobs/upload/${upload_id}/chunk`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              body: fd,
            });
            if (!r.ok) {
              const raw = await r.text();
              let detail = null;
              try { detail = JSON.parse(raw)?.detail; } catch { detail = raw.replace(/<[^>]+>/g, " ").trim().slice(0, 160); }
              throw new Error(detail || `Chunk ${i} failed with ${r.status}`);
            }
            ok = true;
          } catch (e) {
            lastErr = e;
            await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
          }
        }
        if (!ok) throw new Error(lastErr?.message || `Chunk ${i} failed after 3 retries`);
        setProgress((p) => ({
          ...p,
          status: "uploading",
          uploaded_pct: Math.round(((i + 1) / totalChunks) * 100),
        }));
      }

      // ---------- 3. Finalize → server assembles + kicks off the job ----------
      const finFd = new FormData();
      finFd.append("total_chunks", String(totalChunks));
      const finRes = await fetch(`${API}/rebrand-jobs/upload/${upload_id}/finalize`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: finFd,
      });
      if (!finRes.ok) {
        const raw = await finRes.text();
        let detail = null;
        try { detail = JSON.parse(raw)?.detail; } catch { detail = raw.replace(/<[^>]+>/g, " ").trim().slice(0, 160); }
        throw new Error(detail || `Server returned ${finRes.status} on finalize`);
      }
      const job = await finRes.json();
      setProgress({ pages_done: 0, total_pages: job.total_pages || 0, status: "queued", uploaded_pct: 100 });

      // ---------- 4. Poll for progress ----------
      const jobId = job.id;
      let done = false;
      let terminalError = null;
      let consecutivePollFails = 0;
      while (!done) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data } = await api.get(`/rebrand-jobs/${jobId}`);
          consecutivePollFails = 0;
          setProgress({
            pages_done: data.pages_done || 0,
            total_pages: data.total_pages || job.total_pages || 0,
            status: data.status,
            uploaded_pct: 100,
          });
          if (data.status === "done") {
            done = true;
          } else if (data.status === "failed") {
            done = true;
            terminalError =
              data.error ||
              "The rebrand job failed on the server. Please try a smaller PDF or contact support.";
          }
        } catch (pollErr) {
          // Transient network hiccup — retry a few times, then give up loudly.
          consecutivePollFails += 1;
          console.warn("poll error", pollErr);
          if (consecutivePollFails >= 10) {
            done = true;
            terminalError =
              "Lost contact with the server while waiting for the rebrand to finish. Please check your connection and retry.";
          }
        }
      }
      if (terminalError) {
        // Bubble the failure up to the outer try/catch so the user actually sees it.
        throw new Error(terminalError);
      }

      // ---------- 5. Download ----------
      const dlRes = await fetch(`${API}/rebrand-jobs/${jobId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!dlRes.ok) throw new Error("Could not download the rebranded PDF");
      const blob = await dlRes.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name.replace(/\.pdf$/i, "") + "-heda.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert(`Rebrand failed: ${e.message || e}`);
    } finally {
      setRebranding(false);
      setProgress({ pages_done: 0, total_pages: 0, status: "", uploaded_pct: 0 });
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="hed-overline">Brand kit</div>
        <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink md:text-[30px]">
          Turn any supplier PDF into a Heda PDF.
        </h1>
        <p className="mt-1 max-w-2xl text-[13px] text-heda-sub">
          Upload your Heda logos + contact once. Then any supplier brochure you drop below is scanned by
          AI, the supplier&rsquo;s logos and contact details are replaced with yours, and a Heda-branded
          contact page is appended — ready to share with customers.
        </p>
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
        <div className="heda-panel p-5 md:p-7">
          <h2 className="flex items-center gap-2 font-display text-[18px] font-bold text-heda-ink">
            <Save size={18} className="text-heda-gold" /> Brand settings
          </h2>
          <div className="hairline my-4" />
          <div className="space-y-4">
            <div>
              <label className="heda-label" htmlFor="brand-name">Brand name</label>
              <input
                id="brand-name"
                data-testid="brand-name-input"
                value={settings.brand_name}
                onChange={(e) => setSettings({ ...settings, brand_name: e.target.value })}
                className="heda-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <LogoUploader
                label="Horizontal · Color"
                file={logoFiles.horizontal_color}
                onChange={(f) => uploadLogo(f, "horizontal_color")}
                testId="logo-horizontal-color"
              />
              <LogoUploader
                label="Horizontal · White"
                file={logoFiles.horizontal_white}
                onChange={(f) => uploadLogo(f, "horizontal_white")}
                testId="logo-horizontal-white"
              />
              <LogoUploader
                label="Vertical · Color"
                file={logoFiles.vertical_color}
                onChange={(f) => uploadLogo(f, "vertical_color")}
                testId="logo-vertical-color"
              />
              <LogoUploader
                label="Vertical · White"
                file={logoFiles.vertical_white}
                onChange={(f) => uploadLogo(f, "vertical_white")}
                testId="logo-vertical-white"
              />
            </div>
            <p className="text-[11.5px] text-heda-mute">
              Upload transparent PNGs. AI samples each page&rsquo;s background brightness and
              picks the White logo on dark backgrounds, Color on light. Vertical variants are
              used for tall/portrait spaces (e.g. side panels).
            </p>
            <div>
              <label className="heda-label" htmlFor="contact-lines">Contact block (one line each)</label>
              <textarea
                id="contact-lines"
                data-testid="contact-lines-input"
                rows={5}
                placeholder="+91 98xxxxxxxx&#10;hello@hedaplywoods.com&#10;www.hedaplywoods.com&#10;Shop No. 12, Main Road, City"
                value={(settings.contact_lines || []).join("\n")}
                onChange={(e) => setSettings({ ...settings, contact_lines: e.target.value.split("\n") })}
                className="heda-input resize-none"
              />
              <p className="mt-1 text-[11.5px] text-heda-mute">Appears on the final Heda page + wherever supplier contacts are detected.</p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={save}
                disabled={savingSettings}
                data-testid="save-brand-settings"
                className="heda-btn"
              >
                {savingSettings ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
              </button>
              {msg && <span className="text-[13px] text-emerald-700">{msg}</span>}
            </div>
          </div>
        </div>

        <BrandPagePreview settings={settings} refreshKey={previewKey} />

        <div className="heda-panel p-5 md:p-7">
          <h2 className="flex items-center gap-2 font-display text-[18px] font-bold text-heda-ink">
            <UploadCloud size={18} className="text-heda-gold" /> Rebrand a supplier PDF
          </h2>
          <div className="hairline my-4" />
          <label
            data-testid="rebrand-dropzone"
            className={`block cursor-pointer rounded-2xl border-2 border-dashed px-4 py-12 text-center transition ${
              rebranding ? "cursor-wait border-heda-gold bg-heda-gold-tint" : "border-heda-line bg-heda-cream/40 hover:border-heda-gold"
            }`}
          >
            <input
              type="file"
              accept=".pdf,application/pdf"
              disabled={rebranding}
              className="hidden"
              onChange={(e) => rebrand(e.target.files?.[0])}
              data-testid="rebrand-file-input"
            />
            {rebranding ? (
              <div className="space-y-2.5" data-testid="rebrand-progress">
                <div className="inline-flex items-center gap-2 text-sm font-medium text-heda-ink">
                  <Loader2 size={16} className="animate-spin text-heda-gold" />
                  {progress.status === "starting"
                    ? "Preparing upload…"
                    : progress.status === "uploading"
                    ? `Uploading PDF… ${progress.uploaded_pct}%`
                    : progress.status === "queued"
                    ? "Queuing job…"
                    : progress.status === "running"
                    ? `Rebranding page ${progress.pages_done} of ${progress.total_pages || "?"}…`
                    : progress.status === "done"
                    ? "Finishing up…"
                    : "Working…"}
                </div>
                {(progress.status === "uploading" || progress.total_pages > 0) && (
                  <div
                    className="mx-auto h-1.5 w-64 max-w-full overflow-hidden rounded-full bg-white"
                    role="progressbar"
                    aria-valuenow={progress.status === "uploading" ? progress.uploaded_pct : progress.pages_done}
                    aria-valuemin={0}
                    aria-valuemax={progress.status === "uploading" ? 100 : progress.total_pages}
                    data-testid="rebrand-progress-bar"
                  >
                    <div
                      className="h-full bg-heda-gold transition-all"
                      style={{
                        width: `${
                          progress.status === "uploading"
                            ? progress.uploaded_pct
                            : Math.min(100, Math.round((progress.pages_done / Math.max(1, progress.total_pages)) * 100))
                        }%`,
                      }}
                    />
                  </div>
                )}
                <div className="text-[11.5px] text-heda-mute">
                  Large PDFs are chunked into small pieces so any file size works.
                  You can leave this tab open.
                </div>
              </div>
            ) : (
              <>
                <UploadCloud size={30} className="mx-auto mb-3 text-heda-mute" />
                <div className="text-sm font-semibold text-heda-ink">Drop supplier PDF or click to select</div>
                <div className="mt-1 text-[11.5px] text-heda-mute">
                  AI detects supplier logos + contacts → replaces with your Heda brand → downloads as PDF.
                </div>
              </>
            )}
          </label>
          <div className="mt-4 space-y-1 text-[11.5px] leading-relaxed text-heda-mute">
            <div className="flex items-start gap-1.5">
              <Sparkles size={11} className="mt-0.5 flex-shrink-0 text-heda-gold" />
              AI reads each page. Product photos, tech specs and layout are preserved.
            </div>
            <div className="flex items-start gap-1.5">
              <FileText size={11} className="mt-0.5 flex-shrink-0 text-heda-gold" />
              A final Heda contact page is auto-added.
            </div>
            <div className="flex items-start gap-1.5">
              <Download size={11} className="mt-0.5 flex-shrink-0 text-heda-gold" />
              Runs as a background job — no timeout on large PDFs.
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function LogoUploader({ label, file, onChange, testId }) {
  const inputRef = useRef(null);
  return (
    <div>
      <div className="heda-label">{label}</div>
      <div
        onClick={() => inputRef.current?.click()}
        className="flex aspect-[3/2] cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-heda-line bg-heda-cream/50 p-2 transition hover:border-heda-gold"
        data-testid={testId}
      >
        {file ? (
          <img src={fileUrl(file.storage_path)} alt={label} className="max-w-full max-h-full object-contain" />
        ) : (
          <div className="text-center">
            <ImageIcon size={20} className="mx-auto mb-1 text-heda-mute" />
            <div className="text-[11px] text-heda-mute">Click to upload</div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

/**
 * Live preview of the branded LAST PAGE. Fetches the server-rendered PNG
 * whenever `refreshKey` changes (bumped by parent after logo upload / save).
 * Lets the admin sanity-check the design language BEFORE spending time on a
 * large rebrand job.
 */
function BrandPagePreview({ settings, refreshKey }) {
  const [mode, setMode] = useState("light"); // "light" | "dark"
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const objectUrlRef = useRef(null);

  const hasAnyLogo = !!(
    settings.logo_horizontal_color_file_id ||
    settings.logo_horizontal_white_file_id ||
    settings.logo_vertical_color_file_id ||
    settings.logo_vertical_white_file_id
  );

  useEffect(() => {
    if (!hasAnyLogo) {
      setSrc(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const res = await fetch(`${API}/brand-settings/preview.png?dark=${mode === "dark"}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const raw = await res.text();
          let msg = `Preview failed (${res.status})`;
          try { msg = JSON.parse(raw)?.detail || msg; } catch { /* not JSON */ }
          throw new Error(msg);
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = url;
        setSrc(url);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey, mode, hasAnyLogo]);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  return (
    <div className="heda-panel p-5 md:p-7" data-testid="brand-preview">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-[18px] font-bold text-heda-ink">
          <ImageIcon size={18} className="text-heda-gold" /> Brand page preview
        </h2>
        <div className="heda-seg">
          <button
            data-testid="preview-mode-light"
            onClick={() => setMode("light")}
            className={mode === "light" ? "is-on" : ""}
          >
            Color
          </button>
          <button
            data-testid="preview-mode-dark"
            onClick={() => setMode("dark")}
            className={mode === "dark" ? "is-on" : ""}
          >
            White
          </button>
        </div>
      </div>
      <div className="hairline my-4" />
      {!hasAnyLogo ? (
        <div className="rounded-2xl border border-dashed border-heda-line bg-heda-cream/40 px-4 py-16 text-center text-[13px] text-heda-mute">
          Upload at least one logo to see a live preview of your Heda contact page.
        </div>
      ) : loading ? (
        <div className="inline-flex w-full items-center justify-center gap-2 py-16 text-center text-[13px] text-heda-mute">
          <Loader2 size={18} className="animate-spin text-heda-gold" /> Rendering preview…
        </div>
      ) : error ? (
        <div className="heda-error">{error}</div>
      ) : src ? (
        <div className="flex justify-center rounded-2xl border border-heda-line bg-heda-cream/50 p-4">
          <img
            src={src}
            alt="Heda brand page preview"
            data-testid="brand-preview-img"
            className="h-auto max-h-[600px] max-w-full shadow-sm"
          />
        </div>
      ) : null}
      <div className="mt-3 text-[11.5px] text-heda-mute">
        This is the final page that gets appended to every rebranded catalog.
        Toggle between the Color logo (used on light backgrounds) and the White
        logo (used where the source has a dark background).
      </div>
    </div>
  );
}
